import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import Currency from 'currency.js';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { IDeal } from '../../../../modules/advance-application/models/deal.interface';
import { IPromisedPayment } from '../../../../modules/advance-application/models/promised-payment.interface';
import {
  PromisedPaymentStatusEnum,
  PromisedPaymentStatusNames,
} from '../../../../modules/advance-application/models/promised-payment-status.enum';
import { DealService } from '../../../../modules/advance-application/services/deal.service';
import { PromisedPaymentService } from '../../../../modules/advance-application/services/promised-payment.service';
import { V1IBankAccount } from '../../../../modules/authentication/models/v1-api.interface';
import { ConfirmModalService } from '../../../../modules/confirm-modal/confirm-modal.service';
import { CurrencyJsPipe } from '../../../../modules/pipes/currency-js.pipe';
import { DatePipe } from '../../../../modules/pipes/date.pipe';
import { LocalisePipe } from '../../../../modules/pipes/localise.pipe';
import { IBalance } from '../../../../modules/shared/models/balance.interface';
import { BankService } from '../../../../modules/shared/services/bank.service';
import { WorkloadIndicator } from '../../../../modules/shared/services/progress-bar.service';
import { SharedService } from '../../../../modules/shared/services/shared.service';
import { RemoveSubstringPipe } from '../schedule-payment/remove-substring.pipe';

@Component({
  selector: 'app-payment-details',
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.scss'],
  providers: [WorkloadIndicator],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    MatProgressSpinner,
    RemoveSubstringPipe,
  ],
})
export class PaymentDetailsComponent implements OnInit {
  @Input() paymentId: number;

  public payment: IPromisedPayment;
  public deal: IDeal;
  public bank?: V1IBankAccount;
  public readonly promisedPaymentStatusEnum = PromisedPaymentStatusEnum;
  public readonly promisedPaymentStatusNames = PromisedPaymentStatusNames;
  public availableBalance: Currency;
  public remainingBalance: Currency;

  constructor(
    private promisedPaymentService: PromisedPaymentService,
    private dealService: DealService,
    private sharedService: SharedService,
    private toastrService: ToastrService,
    private confirmModalService: ConfirmModalService,
    private bankService: BankService,
    private dialogRef: MatDialogRef<PaymentDetailsComponent>,
    public workloadIndicator: WorkloadIndicator,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private async load(): Promise<void> {
    this.workloadIndicator.start();

    try {
      this.payment = await firstValueFrom(
        this.promisedPaymentService.get(this.paymentId),
      );
      this.deal = await firstValueFrom(
        this.dealService.get(this.payment.dealId),
      );
      const bankList = await firstValueFrom(this.bankService.getList());
      this.bank = bankList.find((b) => b.id === this.payment.bankAccountId);
      this.remainingBalance =
        this.availableBalance.intValue > this.payment.total.intValue
          ? this.availableBalance.subtract(this.payment.total)
          : Currency(0);
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }

    this.workloadIndicator.stop();
  }

  public cancelPayment(): void {
    this.confirmModalService.openConfirm({
      confirmAction: () => {
        this.workloadIndicator.start();

        this.promisedPaymentService.cancel(this.paymentId).subscribe(
          () => {
            this.dialogRef.close(true);
          },
          (e) => {
            this.toastrService.error(this.sharedService.getErrorText(e, true));
            this.workloadIndicator.stop();
          },
        );
      },
      title: 'Cancel scheduled payment?',
      bodyText:
        'Please confirm that you would like to cancel this scheduled payment.',
      confirmText: 'Yes',
      variant: 'info',
      width: '520px',
    });
  }
}
